import {FC, ReactNode} from 'react';
import {Grid} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import TrendingFlatOutlinedIcon from '@mui/icons-material/TrendingFlatOutlined';
import {useIntl} from 'react-intl';
import {useBusinessCurrency} from 'src/app/hooks';
import {ContractsSummaryDTO, InvoicesSummaryDTO} from 'src/types';
import {KpiCard} from 'src/pages/EnergyConsumptionReport/components/KpiCard';

interface BusinessKpiGridProps {
    invoices?: InvoicesSummaryDTO;
    contracts?: ContractsSummaryDTO;
    loading?: boolean;
}

const formatInvoiceMoney = (raw: number): string =>
    (raw / 10000).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});

const formatMrrMoney = (raw: number): string =>
    (raw / 100).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});

const formatCount = (n: number): string => n.toLocaleString();

const formatNet = (net: number): string => {
    if (net > 0) return `+${formatCount(net)}`;
    if (net < 0) return formatCount(net);
    return '0';
};

const pickNetIcon = (net: number): ReactNode => {
    if (net > 0) return <TrendingUpOutlinedIcon fontSize="small"/>;
    if (net < 0) return <TrendingDownOutlinedIcon fontSize="small"/>;
    return <TrendingFlatOutlinedIcon fontSize="small"/>;
};

export const BusinessKpiGrid: FC<BusinessKpiGridProps> = ({
                                                              invoices,
                                                              contracts,
                                                              loading,
                                                          }) => {
    const intl = useIntl();
    // Both summaries arrive keyed by currency, so this is a lookup key, not a label: asking for CHF in
    // a business that bills in hryvnia found no entry and showed every figure as zero.
    const currency = useBusinessCurrency();

    const revenueRaw = invoices?.byStatus?.PAID?.amountByCurrency?.[currency] ?? 0;
    const outstandingRaw = invoices?.byStatus?.UNPAID?.amountByCurrency?.[currency] ?? 0;
    const mrrRaw = contracts?.totalMRR?.[currency] ?? 0;
    const activeContracts = contracts?.totalActive ?? 0;
    const newInPeriod = contracts?.newInPeriod ?? 0;
    const archivedInPeriod = contracts?.archivedInPeriod ?? 0;
    const netGrowth = newInPeriod - archivedInPeriod;

    const cards = [
        {
            label: intl.formatMessage({id: 'kpiRevenue', defaultMessage: 'Revenue'}),
            value: formatInvoiceMoney(revenueRaw),
            unit: currency,
            icon: <PaymentsOutlinedIcon fontSize="small"/>,
        },
        {
            label: intl.formatMessage({id: 'kpiOutstanding', defaultMessage: 'Outstanding'}),
            value: formatInvoiceMoney(outstandingRaw),
            unit: currency,
            icon: <HourglassTopOutlinedIcon fontSize="small"/>,
        },
        {
            label: intl.formatMessage({id: 'kpiMrr', defaultMessage: 'MRR'}),
            value: formatMrrMoney(mrrRaw),
            unit: `${currency}/mo`,
            icon: <AutoGraphOutlinedIcon fontSize="small"/>,
        },
        {
            label: intl.formatMessage({id: 'kpiActiveContracts', defaultMessage: 'Active Contracts'}),
            value: formatCount(activeContracts),
            icon: <DescriptionOutlinedIcon fontSize="small"/>,
        },
        {
            label: intl.formatMessage({id: 'kpiNetGrowth', defaultMessage: 'Net Growth'}),
            value: formatNet(netGrowth),
            icon: pickNetIcon(netGrowth),
        },
    ];

    return (
        <Grid container spacing={2}>
            {cards.map((card) => (
                <Grid item xs={6} sm={4} md={2.4} key={card.label}>
                    <KpiCard
                        label={card.label}
                        value={card.value}
                        unit={card.unit}
                        icon={card.icon}
                        loading={loading}
                    />
                </Grid>
            ))}
        </Grid>
    );
};
